import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  VideoCameraIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { mediaApi } from "../api/cmsApi";
import toast from "react-hot-toast";

interface MediaEmbed {
  _id: string;
  key: string;
  title: string;
  description: string | null;
  embedUrl: string;
  platform: string;
  thumbnailUrl: string | null;
  duration: number | null;
  placement: string;
  isActive: boolean;
}

const platforms = [
  { id: "wistia", label: "Wistia" },
  { id: "youtube", label: "YouTube" },
  { id: "vimeo", label: "Vimeo" },
];

const placements = [
  { id: "hero-default", label: "Hauptseite (Standard)", description: "Video im Hero der Standard-Landingpage" },
  { id: "hero-facebook", label: "Facebook", description: "Video im Hero der Facebook-Landingpage" },
  { id: "hero-instagram", label: "Instagram", description: "Video im Hero der Instagram-Landingpage" },
  { id: "hero-x", label: "X (Twitter)", description: "Video im Hero der X-Landingpage" },
  { id: "hero-linkedin", label: "LinkedIn", description: "Video im Hero der LinkedIn-Landingpage" },
  { id: "hero-reddit", label: "Reddit", description: "Video im Hero der Reddit-Landingpage" },
  { id: "hero-tiktok", label: "TikTok", description: "Video im Hero der TikTok-Landingpage" },
];

export default function Media() {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    key: "",
    title: "",
    description: "",
    embedUrl: "",
    platform: "wistia",
    thumbnailUrl: "",
    duration: "",
    placement: "hero-default",
  });

  const { data: mediaEmbeds = [], isLoading } = useQuery({
    queryKey: ["media"],
    queryFn: () => mediaApi.list().then((res) => res.data.data || []),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      mediaApi.create({
        ...data,
        description: data.description || null,
        thumbnailUrl: data.thumbnailUrl || null,
        duration: data.duration ? parseInt(data.duration) : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] });
      setIsCreating(false);
      resetForm();
      toast.success("Video erstellt");
    },
    onError: () => toast.error("Fehler beim Erstellen"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: Partial<MediaEmbed> }) =>
      mediaApi.update(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] });
      setEditingKey(null);
      resetForm();
      toast.success("Video aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => mediaApi.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] });
      toast.success("Video gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  const initMutation = useMutation({
    mutationFn: () => mediaApi.init(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] });
      toast.success("Standard-Videos geladen");
    },
    onError: () => toast.error("Fehler beim Initialisieren"),
  });

  const resetForm = () => {
    setFormData({
      key: "",
      title: "",
      description: "",
      embedUrl: "",
      platform: "wistia",
      thumbnailUrl: "",
      duration: "",
      placement: "hero-default",
    });
  };

  const handleCreate = () => {
    createMutation.mutate(formData);
  };

  const handleUpdate = (key: string) => {
    updateMutation.mutate({
      key,
      data: {
        title: formData.title,
        description: formData.description || null,
        embedUrl: formData.embedUrl,
        platform: formData.platform,
        thumbnailUrl: formData.thumbnailUrl || null,
        duration: formData.duration ? parseInt(formData.duration) : null,
        placement: formData.placement,
      },
    });
  };

  const handleToggleActive = (media: MediaEmbed) => {
    updateMutation.mutate({
      key: media.key,
      data: { isActive: !media.isActive },
    });
  };

  const startEditing = (media: MediaEmbed) => {
    setEditingKey(media.key);
    setFormData({
      key: media.key,
      title: media.title,
      description: media.description || "",
      embedUrl: media.embedUrl,
      platform: media.platform,
      thumbnailUrl: media.thumbnailUrl || "",
      duration: media.duration?.toString() || "",
      placement: media.placement,
    });
  };

  const getPlacementLabel = (placement: string) => {
    return placements.find((p) => p.id === placement)?.label || placement;
  };

  const getPlatformLabel = (platform: string) => {
    return platforms.find((p) => p.id === platform)?.label || platform;
  };

  const getVideoPreview = (embedUrl: string, platform: string) => {
    if (platform === "wistia") {
      const match = embedUrl.match(/wistia\.com\/medias\/([a-z0-9]+)/i);
      if (match) {
        return `https://fast.wistia.com/embed/medias/${match[1]}/swatch`;
      }
    } else if (platform === "youtube") {
      const match = embedUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (match) {
        return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
      }
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <VideoCameraIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Media</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalte Videos und andere eingebettete Medien
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {mediaEmbeds.length === 0 && (
            <button
              onClick={() => initMutation.mutate()}
              disabled={initMutation.isPending}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {initMutation.isPending ? "Initialisiere..." : "Standard-Videos laden"}
            </button>
          )}
          <button
            onClick={() => {
              setIsCreating(true);
              resetForm();
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Neues Video
          </button>
        </div>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Neues Video hinzufügen</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schlüssel (eindeutig)</label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="z.B. hero-video"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Nur Kleinbuchstaben, Zahlen und Bindestriche
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plattform</label>
                  <select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {platforms.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="z.B. So funktioniert ClaroClaim"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Video-URL</label>
                <input
                  type="url"
                  value={formData.embedUrl}
                  onChange={(e) => setFormData({ ...formData, embedUrl: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="z.B. https://talentschmiede.wistia.com/medias/l5eleu09lr"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platzierung</label>
                  <select
                    value={formData.placement}
                    onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {placements.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dauer (Sekunden)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  rows={2}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isPending || !formData.key || !formData.title || !formData.embedUrl}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
                >
                  {createMutation.isPending ? "Erstellen..." : "Erstellen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">Laden...</div>
        ) : mediaEmbeds.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
            <VideoCameraIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Noch keine Videos vorhanden.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Erstes Video hinzufügen
            </button>
          </div>
        ) : (
          mediaEmbeds.map((media: MediaEmbed) => (
            <div key={media._id} className="bg-white dark:bg-gray-800 shadow rounded-lg">
              {editingKey === media.key ? (
                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Video-URL</label>
                      <input
                        type="url"
                        value={formData.embedUrl}
                        onChange={(e) => setFormData({ ...formData, embedUrl: e.target.value })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plattform</label>
                        <select
                          value={formData.platform}
                          onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                          className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          {platforms.map((p) => (
                            <option key={p.id} value={p.id}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platzierung</label>
                        <select
                          value={formData.placement}
                          onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                          className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          {placements.map((p) => (
                            <option key={p.id} value={p.id}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setEditingKey(null)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" />
                        Abbrechen
                      </button>
                      <button
                        onClick={() => handleUpdate(media.key)}
                        disabled={updateMutation.isPending}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex gap-4">
                    {/* Video Preview */}
                    <div className="w-32 h-20 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0 overflow-hidden relative">
                      {getVideoPreview(media.embedUrl, media.platform) ? (
                        <img
                          src={getVideoPreview(media.embedUrl, media.platform) || ""}
                          alt={media.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-600">
                          <VideoCameraIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <PlayIcon className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{media.title}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{media.key}</code>
                            <span className="mx-2">•</span>
                            {getPlatformLabel(media.platform)}
                            <span className="mx-2">•</span>
                            {getPlacementLabel(media.placement)}
                          </p>
                          {media.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{media.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleActive(media)}
                            className={`
                              px-3 py-1 rounded-full text-xs font-medium
                              ${media.isActive
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                              }
                            `}
                          >
                            {media.isActive ? "Aktiv" : "Inaktiv"}
                          </button>
                          <a
                            href={media.embedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </a>
                          <button
                            onClick={() => startEditing(media)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm("Video wirklich löschen?")) {
                                deleteMutation.mutate(media.key);
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
